/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2023-07-25 16:33:48
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './index.scss'
import { FilterType, POIItemOverView } from '../../index'
import { fetchPhotosDetail } from '../../../../actions/photoLocation'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import classNames from 'classnames'
import classnames from 'classnames'
import dayjs from 'dayjs'
import CONFIG from '../../../../constants/config'
import { Button, Spin } from 'antd'
import CheckMoreImg from '../../../../images/check-more-icon.png'
import Viewer from '../../../components/ViewerV2'
import {
  EnvironmentFilled,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons'

interface IProps {
  currentOverView: POIItemOverView
  filterData: FilterType
  visible: boolean
  setVisible: (...args: any[]) => any
  sensorsClick: (clickItem: string) => void
  handleClose: () => void
}
// 照片详情必要的属性
type PhotoDetailItem = {
  addr: string // 地址
  largeURL: string // 大图
  smallURL: string // 小图
  mediaType: number // 类型 0:照片 1:视频
  time: number // 时间戳毫秒级
  userID: string // 用户ID
  userNickname: string // 用户昵称
  videoURL: string // 视频地址
  [key: string]: any
}
// 返回数据格式
type PhotoAddrDetailResp = {
  datePhotos: {
    [key: string]: string // 日期照片数键值对 key:日期  value: 照片数量
  }
  pageCond: string // 分页参数
  photoList: Array<PhotoDetailItem>
  dateMapPhotoList: Map<string, Array<PhotoDetailItem>> // 日期照片集合键值对 key:日期  value: 照片集合
  [key: string]: any
}

interface IPhotosByDayProps {
  dateKey: string
  list: Array<PhotoDetailItem>
  index: number
  sensorsClick: (clickItem: string) => void
  setViewPhotos: (list: Array<PhotoDetailItem>) => void
  viewerRef: any
}

const getSmallUrl = (url) => {
  if (url.includes('?x-oss-process')) return url + ',w_100'
  return url
}

const PhotosByDay: React.FC<IPhotosByDayProps> = (props) => {
  const { dateKey, list, index, sensorsClick, setViewPhotos, viewerRef } = props
  const daysDiff = dayjs
    .unix(parseInt(String(Date.now() / 1000)))
    .diff(dayjs.unix(list[0].time), 'days', true)
  const [highlight, setHighLight] = useState(false)

  useEffect(() => {
    setHighLight(true)
    const timer = setTimeout(() => {
      setHighLight(false)
    }, 1000)
    return () => {
      clearTimeout(timer)
    }
  }, [list.length])

  return (
    <div className="detail-card-content-item" key={index}>
      <div className="item-header">
        <span className="date-tip">
          {dateKey}{' '}
          <span
            className={classnames('photo-num', {
              'photo-num-changed': highlight
            })}
          >
            （{list.length}张）
          </span>
        </span>
        <span>
          距今<span>{Math.round(daysDiff)}</span>天
        </span>
      </div>
      <div className="user-tip">{list[0].userNickname}在该点拍照</div>
      <div className="img-list">
        {list.slice(0, 6).map((item, imgIndex) => (
          <div className="img-wrapper" key={imgIndex}>
            <img
              src={getSmallUrl(item.smallURL)}
              alt=""
              onClick={() => {
                if (list.length > 6 && imgIndex === 5) return
                sensorsClick('photo')
                setViewPhotos(list)
                setTimeout(() => {
                  viewerRef.current.view(imgIndex)
                })
              }}
            />
          </div>
        ))}
        {list.length > 6 && (
          <div
            className="check-more"
            onClick={(e) => {
              e.stopPropagation()
              sensorsClick('morePhoto')
              setViewPhotos(list)
              setTimeout(() => {
                viewerRef.current.view(5)
              })
            }}
          >
            <img src={CheckMoreImg} alt="" />
            <span>查看更多</span>
          </div>
        )}
      </div>
    </div>
  )
}

const POIDetailCard: React.FC<IProps> = ({
  currentOverView,
  filterData,
  visible,
  setVisible,
  sensorsClick,
  handleClose
}) => {
  const { name, addrName, extra = {}, fmtAddr } = currentOverView
  const dispatch = useAppDispatch()
  const groupState = useAppSelector((state) => state.group)
  const { groupId } = groupState
  const [photoData, setPhotoData] = useState<PhotoAddrDetailResp>({
    photoList: [] as Array<PhotoDetailItem>,
    pageCond: '',
    datePhotos: {},
    dateMapPhotoList: new Map<string, Array<PhotoDetailItem>>()
  })
  const requestEnd = useRef(false)
  const [loading, setLoading] = useState(false)
  const viewerRef = useRef<any>(null)
  const [viewPhotos, setViewPhotos] = useState<Array<PhotoDetailItem>>([])

  useEffect(() => {
    if (!visible) return
    requestEnd.current = false
    setPhotoData({
      photoList: [] as Array<PhotoDetailItem>,
      pageCond: '',
      datePhotos: {},
      dateMapPhotoList: new Map<string, Array<PhotoDetailItem>>()
    })
    requestData()
  }, [visible, filterData, currentOverView])

  const requestData = useCallback(
    (pageCond = '') => {
      if (requestEnd.current || loading) return
      setLoading(true)
      const { users, userIDList, departmentIDList, times } = filterData
      const params = {
        userIDList,
        users,
        departmentIDList: departmentIDList.map((item) => item.id),
        startDate: times.start.format('YYYY-MM-DD'),
        endDate: times.end.format('YYYY-MM-DD'),
        groupID: groupId,
        pageCond,
        standardLocationInfo: {
          country: '中国',
          province: extra.province,
          district: extra.district,
          city: extra.city,
          poi: name
          // "township": "天府三街"
        }
      }
      dispatch(fetchPhotosDetail(params))
        .then((res) => {
          setLoading(false)
          requestEnd.current = res.pageCond === ''
          setPhotoData((prevState) => {
            const photoList = prevState.photoList.concat(res.photoList)
            const dateMapPhotoList = prevState.dateMapPhotoList
            res.photoList
              .sort((a, b) => b.time - a.time)
              .forEach((item) => {
                const dateKey = dayjs
                  .unix(item.time)
                  .format(CONFIG.dateFormatZH)
                const arr = dateMapPhotoList.get(dateKey) || []
                arr.push(item)
                dateMapPhotoList.set(dateKey, arr)
              })
            return {
              ...prevState,
              pageCond: res.pageCond,
              photoList,
              dateMapPhotoList
            }
          })
        })
        .catch((e) => setLoading(false))
    },
    [filterData, dispatch, extra, groupId, name, loading]
  )

  // 滚动到底部加载
  const handleContentScroll = useCallback(
    (e) => {
      const { clientHeight, scrollHeight, scrollTop } = e.target
      if (Math.ceil(scrollHeight - Math.ceil(scrollTop)) <= clientHeight - 1) {
        requestData(photoData.pageCond)
      }
    },
    [requestData, photoData]
  )

  if (currentOverView.level !== 'poi') return null

  return (
    <Spin
      spinning={loading}
      wrapperClassName={classNames('poi-detail-card', {
        close: !visible
      })}
    >
      <div className="detail-card-header">
        <Button type="link" onClick={handleClose}>
          <LeftOutlined />
          返回
        </Button>
        <div className="header-text">
          <span>{addrName}</span>
        </div>
        <div className="statistic-info">
          <span>
            {filterData.times.start.format('M.DD') +
              '-' +
              filterData.times.end.format('M.DD')}
          </span>
          <span>
            共到访<span>{currentOverView.photoTotalDate}</span>天
          </span>
          <span>
            <span>{currentOverView.photoUserCnt}</span>人在此拍照
          </span>
        </div>
        <div className="position-info">
          <EnvironmentFilled />
          <span>{fmtAddr}</span>
        </div>
      </div>
      <div className="detail-card-content" onScroll={handleContentScroll}>
        {Array.from(photoData.dateMapPhotoList).map(
          ([dateKey, list], index) => (
            <PhotosByDay
              key={index}
              dateKey={dateKey}
              list={list}
              index={index}
              sensorsClick={sensorsClick}
              setViewPhotos={setViewPhotos}
              viewerRef={viewerRef}
            />
          )
        )}
        <div style={{ height: 120 }}>
          {photoData.pageCond ? (
            <Button type="link" onClick={() => requestData(photoData.pageCond)}>
              更多照片
              <RightOutlined />
            </Button>
          ) : (
            <p
              style={{
                fontSize: '12px',
                color: '#646566',
                textAlign: 'center'
              }}
            >
              没有更多了
            </p>
          )}
        </div>
      </div>
      <Viewer
        ref={viewerRef}
        photos={viewPhotos}
        canDelete={false}
        canRectify={false}
        canAiSecurity={false}
        canFavorite={false}
      />
    </Spin>
  )
}

export default POIDetailCard
